import React, { createContext, useContext, useRef, useState } from 'react';
import { Notification } from '../../Notifications/types';
import { Notifications } from '../../Notifications/Notifications';

type Value = {
  pushNotification: (notification: Notification) => void;
  removeNotification: (notification: Notification) => void;
  removeAllNotifications: () => void;
  preventNotifications: () => void;
  enableNotifications: () => void;
};

const errorMessage = 'Notification context is missing a Provider';

const defaultValue: Value = {
  pushNotification: () => {
    throw new Error(errorMessage);
  },
  removeNotification: () => {
    throw new Error(errorMessage);
  },
  removeAllNotifications: () => {
    throw new Error(errorMessage);
  },
  preventNotifications: () => {
    throw new Error(errorMessage);
  },
  enableNotifications: () => {
    throw new Error(errorMessage);
  },
};

export const NotificationContext = createContext(defaultValue);

export const NotificationProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const enabled = useRef(true);

  const handleNotification = (newNotifications: Notification[]) => {
    if (enabled.current) {
      setNotifications(newNotifications);
    }
  };

  const pushNotification = (toPush: Notification) => {
    if (!enabled.current) return;

    const existing = notifications.find(
      notification => notification.id === toPush.id
    );
    if (existing) {
      return;
    }
    handleNotification([toPush, ...notifications]);
  };

  const removeNotification = (toRemove: Notification) => {
    handleNotification(
      notifications.filter(notification => notification.id !== toRemove.id)
    );
  };

  const removeAllNotifications = () => handleNotification([]);

  const preventNotifications = () => {
    enabled.current = false;
  };
  const enableNotifications = () => {
    enabled.current = true;
  };

  return (
    <NotificationContext.Provider
      value={
        {
          pushNotification,
          removeNotification,
          removeAllNotifications,
          preventNotifications,
          enableNotifications,
        } as Value
      }
    >
      {children}

      <Notifications
        notifications={notifications}
        removeNotification={removeNotification}
      />
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => useContext(NotificationContext);
